import type {CommissionOrderType} from "@reva/shared/value-order";

import type {
    CommissionCertificate,
    CommissionOwnerData,
    CommissionRegisterData,
    CommissionUnitNumbers,
    CommissionValueOrder,
    ContactPerson,
} from "../../api/payload-types/commission-value-order";
import {isValidEmail} from "../../util/emailUtil";
import {createBlankPdfFile} from "./blankPdf";

export const DOCUMENT_OPTIONS = [
    "landRegister",
    "declarationOfCondominium",
    "informationOnContaminatedSites",
    "partitionPlan",
    "informationOnBuildingEncumbrances",
    "registrationApproval",
    "officialCadastralMap",
    "nonOfficialCadastralMap",
];

export const INTERIOR_OPTIONS = ["GrossFloorArea", "FloorPlan", "LivingSpaceCalculation"];
export const PARCEL_OPTIONS = ["landRegister", "informationOnContaminatedSites", "informationOnBuildingEncumbrances"];
export const FLURKARTE_OPTIONS = ["officialCadastralMap", "nonOfficialCadastralMap"];

export const MAX_CONTACT_NAME_LENGTH = 90;
const MAX_FILE_BASE_NAME_LENGTH = 200;

export type Certificate = CommissionCertificate;
export type Contact = ContactPerson;
export type OwnerData = CommissionOwnerData;
export type OwnerType = "legal" | "natural";
export type RegisterData = CommissionRegisterData;
export type UnitNumbers = CommissionUnitNumbers;

export interface DocumentData {
    declarationOfCondominium: UnitNumbers & {certificates: Certificate[]};
    partitionPlan: UnitNumbers;
    registrationApproval: {description: string | null; certificates: Certificate[]};
    parcelInfo: {entries: (string | null)[]; parcelNumber: string | null};
}

export const EMPTY_CERT = (): Certificate => ({number: null, date: null});
export const EMPTY_CONTACT = (): Contact => ({firstName: null, lastName: null, phoneMobile: null, email: null});
export const EMPTY_OWNER_DATA = (): OwnerData => ({
    ownerLegalEntity: null,
    ownerFirstName: null,
    ownerSurname: null,
    ownerDateOfBirth: null,
});
export const EMPTY_REGISTER_DATA = (): RegisterData => ({
    amtsgericht: null,
    grundbuch: null,
    gemarkung: null,
    blattnummer: null,
    bemerkungen: null,
});
export const EMPTY_DOC_SPECIFIC = (): DocumentData => ({
    declarationOfCondominium: {
        certificates: [EMPTY_CERT()],
        apartmentNumber: null,
        basementNumber: null,
        parkingNumber: null,
    },
    partitionPlan: {apartmentNumber: null, basementNumber: null, parkingNumber: null},
    registrationApproval: {description: null, certificates: [EMPTY_CERT()]},
    parcelInfo: {entries: [null], parcelNumber: null},
});

export interface CommissionOrderForm {
    orderType: CommissionOrderType | null;
    mainContact: Contact;
    additionalContacts: Contact[];
    notes: string | null;
    interiorOptions: string[];
    documentOptions: string[];
    registerData: RegisterData;
    documentData: DocumentData;
    ownerType: OwnerType | null;
    ownerData: OwnerData;
    legitimateInterest: boolean;
    creditorRegistered: boolean | null;
    legalDocument: File | null;
}

export type FormChange = Partial<CommissionOrderForm> | ((prev: CommissionOrderForm) => Partial<CommissionOrderForm>);

export const EMPTY_FORM = (): CommissionOrderForm => ({
    orderType: null,
    mainContact: EMPTY_CONTACT(),
    additionalContacts: [],
    notes: null,
    interiorOptions: [],
    documentOptions: [],
    registerData: EMPTY_REGISTER_DATA(),
    documentData: EMPTY_DOC_SPECIFIC(),
    ownerType: null,
    ownerData: EMPTY_OWNER_DATA(),
    legitimateInterest: false,
    creditorRegistered: null,
    legalDocument: null,
});

export const hasLegalInputs = (orderType: CommissionOrderType | null, documentOptions: string[]): boolean =>
    orderType === "DocumentCollection" && documentOptions.some((o) => !FLURKARTE_OPTIONS.includes(o));

const toCertificatesPayload = (certificates: Certificate[]): Certificate[] =>
    certificates.filter((c) => c.date).map((c) => ({number: c.number || null, date: `${c.date}T00:00:00`}));

export function buildDocSpecificPayload(
    documentOptions: string[],
    documentData: DocumentData,
): NonNullable<CommissionValueOrder["documentTypeData"]> {
    return {
        declarationOfCondominium: documentOptions.includes("declarationOfCondominium")
            ? {
                  certificates: toCertificatesPayload(documentData.declarationOfCondominium.certificates),
                  additions: [
                      {label: "ETW-Nummer", val: documentData.declarationOfCondominium.apartmentNumber},
                      {label: "Kellernummer", val: documentData.declarationOfCondominium.basementNumber},
                      {label: "Stellplatz-/Garagennummer", val: documentData.declarationOfCondominium.parkingNumber},
                  ]
                      .filter(({val}) => val)
                      .map(({label, val}) => `${label}: ${val}`)
                      .join(", "),
              }
            : undefined,
        partitionPlan: documentOptions.includes("partitionPlan") ? documentData.partitionPlan : undefined,
        registrationApproval: documentOptions.includes("registrationApproval")
            ? {
                  description: documentData.registrationApproval.description,
                  certificates: toCertificatesPayload(documentData.registrationApproval.certificates),
              }
            : undefined,
        parcelInfo: PARCEL_OPTIONS.some((o) => documentOptions.includes(o))
            ? {
                  entries: documentData.parcelInfo.entries.filter((e) => e),
                  parcelNumber: documentData.parcelInfo.parcelNumber || null,
              }
            : undefined,
    };
}

/** Nur die Felder der gewählten Eigentümerart; Eingaben zur anderen Art bleiben nach einem Wechsel im State stehen. */
export function buildOwnerPayload(ownerType: OwnerType | null, ownerData: OwnerData): OwnerData {
    if (ownerType === "natural") return {...ownerData, ownerLegalEntity: null};
    if (ownerType === "legal") return {...EMPTY_OWNER_DATA(), ownerLegalEntity: ownerData.ownerLegalEntity};
    return EMPTY_OWNER_DATA();
}

/** Sendet nur, was für die gewählte Auftragsart und Optionen im Dialog sichtbar ist. */
export function buildCommissionOrder(
    form: CommissionOrderForm & {orderType: CommissionOrderType},
    propertyType: string,
): CommissionValueOrder {
    const isDocumentCollection = form.orderType === "DocumentCollection";
    const legal = hasLegalInputs(form.orderType, form.documentOptions);
    return {
        orderType: form.orderType,
        propertyType,
        mainContact: form.orderType === "InteriorInspection" ? form.mainContact : null,
        additionalContacts: isDocumentCollection ? [] : form.additionalContacts,
        notes: form.orderType === "InteriorInspection" ? form.notes : null,
        orderOptions: [...form.interiorOptions, ...form.documentOptions],
        legalData: legal ? {...form.registerData, ...buildOwnerPayload(form.ownerType, form.ownerData)} : null,
        documentTypeData: legal ? buildDocSpecificPayload(form.documentOptions, form.documentData) : null,
        ownerDocument: legal ? (form.creditorRegistered ? createBlankPdfFile() : form.legalDocument) : null,
        registerExtract: legal && form.creditorRegistered ? form.legalDocument : null,
    };
}

/** Die Orders-API verlangt den Dateinamen ohne Endung mit 1–200 Zeichen. */
export const isValidUploadFileName = (fileName: string): boolean => {
    const dot = fileName.lastIndexOf(".");
    const baseName = dot >= 0 ? fileName.slice(0, dot) : fileName;
    return baseName.length > 0 && baseName.length <= MAX_FILE_BASE_NAME_LENGTH;
};

export const isValidPhone = (phone: string | null | undefined): boolean => {
    const d = phone?.replace(/[\s\-().]/g, "");
    const n = d?.startsWith("+49") ? `0${d.slice(3)}` : d?.startsWith("0049") ? `0${d.slice(4)}` : d;
    return /^01[5-7]\d{7,9}$/.test(n ?? "");
};
export const hasContactWay = (contact: Contact): boolean =>
    isValidPhone(contact.phoneMobile) || (!!contact.email && isValidEmail(contact.email));
const isValidContact = (contact: Contact): boolean => !!contact.lastName && hasContactWay(contact);

/** Ist die Liste noch ganz leer, wird nur die erste Zeile markiert, nicht auch die leere Neu-Zeile. */
export const isCertificateDateMissing = (certificates: Certificate[], idx: number): boolean => {
    const certificate = certificates[idx];
    if (certificate.date) return false;
    return !!certificate.number || (idx === 0 && !certificates.some((c) => c.date || c.number));
};

export const areCertificatesValid = (certificates: Certificate[]): boolean =>
    certificates.some((c) => c.date) && certificates.every((c) => c.date || !c.number);

export function validateCommissionOrder(form: CommissionOrderForm): boolean {
    const {orderType, documentOptions, documentData, registerData} = form;
    if (!orderType) return false;
    if (orderType !== "DocumentCollection") {
        if (orderType === "InteriorInspection" && !isValidContact(form.mainContact)) return false;
        return form.additionalContacts.every(isValidContact);
    }

    if (documentOptions.length === 0) return false;
    if (!hasLegalInputs(orderType, documentOptions)) return true;

    if (!registerData.grundbuch || !registerData.amtsgericht || !registerData.gemarkung || !registerData.blattnummer)
        return false;
    if (!form.legitimateInterest) return false;
    if (form.creditorRegistered === null || !form.legalDocument) return false;
    if (!isOwnerInfoValid(form.ownerType, form.ownerData)) return false;

    if (
        documentOptions.includes("declarationOfCondominium") &&
        !areCertificatesValid(documentData.declarationOfCondominium.certificates)
    )
        return false;

    if (
        documentOptions.includes("registrationApproval") &&
        (!documentData.registrationApproval.description ||
            !areCertificatesValid(documentData.registrationApproval.certificates))
    )
        return false;

    if (PARCEL_OPTIONS.some((o) => documentOptions.includes(o)) && !documentData.parcelInfo.entries.some((e) => !!e))
        return false;

    return true;
}

export function isOwnerInfoValid(ownerType: OwnerType | null, ownerData: OwnerData): boolean {
    if (!ownerType) return false;
    if (ownerType === "legal" && !ownerData.ownerLegalEntity) return false;
    if (ownerType === "natural" && !ownerData.ownerFirstName) return false;
    if (ownerType === "natural" && !ownerData.ownerSurname) return false;
    if (ownerType === "natural" && !ownerData.ownerDateOfBirth) return false;
    return true;
}

/**
 * Teilt die Optionen einer Auftragsart in freigeschaltete und nicht freigeschaltete.
 * @param options Optionen in Anzeigereihenfolge
 * @param allowed freigeschaltete Optionen; null/undefined = alle
 */
export function splitByAllowed(
    options: string[],
    allowed: string[] | null | undefined,
): {offered: string[]; notEnabled: string[]} {
    if (!allowed) return {offered: options, notEnabled: []};
    return {
        offered: options.filter((o) => allowed.includes(o)),
        notEnabled: options.filter((o) => !allowed.includes(o)),
    };
}

// Pflichtfeld-Markierung: solange etwas fehlt und über "Beauftragen" gehovert bzw. darauf getippt wurde.
export function shouldShowInvalid({
    isValid,
    hovering,
    touched,
}: {
    isValid: boolean;
    hovering: boolean;
    touched: boolean;
}): boolean {
    return !isValid && (hovering || touched);
}
